import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
} from "react";

import { useControllableState } from "@/hooks/use-controllable-state";
import { useStack } from "@/hooks/use-stack";
import { runIfFn } from "@/lib/function";

interface ScreenManagerState {
  currentScreen: string;
  setCurrentScreen: (screen: string) => void;
  goBack: () => void;
  canGoBack: boolean;
}

const ScreenManagerContext = createContext<ScreenManagerState | undefined>(
  undefined,
);

function useScreenManager() {
  const context = useContext(ScreenManagerContext);
  if (!context) {
    throw new Error(
      "useScreenManager must be used within a ScreenManagerProvider",
    );
  }
  return context;
}

interface ScreenManagerProps {
  defaultScreen?: string;
  currentScreen?: string;
  children: ReactNode | ((arg: ScreenManagerState) => ReactNode);
  onChangeScreen?: (screen: string) => void;
}

export function ScreenManager({
  defaultScreen,
  currentScreen: currentScreenProp,
  onChangeScreen,
  ...otherProps
}: ScreenManagerProps) {
  const [history, { push, pop, peek }] = useStack<string>();
  const [currentScreen, setCurrentScreen] = useControllableState({
    defaultValue: defaultScreen,
    value: currentScreenProp,
    onChange: (nextScreen) => {
      push(currentScreen);
      onChangeScreen?.(nextScreen);
    },
  });

  const goBack = useCallback(() => {
    const previousScreen = peek();
    if (previousScreen) {
      setCurrentScreen(previousScreen);
      pop();
    }
  }, [peek, pop, setCurrentScreen]);

  const canGoBack = useMemo(() => history.length > 0, [history.length]);

  const context = useMemo(
    () => ({ currentScreen, setCurrentScreen, goBack, canGoBack }),
    [canGoBack, currentScreen, goBack, setCurrentScreen],
  );

  return (
    <ScreenManagerContext.Provider value={context}>
      {runIfFn(otherProps.children, context)}
    </ScreenManagerContext.Provider>
  );
}

interface ScreenProps {
  screenName: string | string[];
  children: ReactNode | ((arg: ScreenManagerState) => ReactNode);
}

export function Screen({ children, screenName }: ScreenProps) {
  const screenManagerContext = useScreenManager();

  const isActive = Array.isArray(screenName)
    ? screenName.some((name) => name === screenManagerContext.currentScreen)
    : screenName === screenManagerContext.currentScreen;

  return <>{isActive ? runIfFn(children, screenManagerContext) : null}</>;
}
